import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api.ts";
import { keys } from "../lib/query.ts";

async function unwrap<T>(promise: Promise<{ data: T | null; error: unknown }>): Promise<T> {
  const { data, error } = await promise;
  if (error !== null) throw (error as { value?: unknown }).value ?? error;
  if (data === null) throw new Error("the API returned no body");
  return data;
}

type Franchise = ReturnType<typeof api.v1.franchises>;

export type StaffNotificationFeed = NonNullable<
  Awaited<ReturnType<Franchise["staff-notifications"]["get"]>>["data"]
>;
export type StaffNotificationView = StaffNotificationFeed["data"][number];

export type NotificationPreferences = NonNullable<
  Awaited<ReturnType<(typeof api.v1.me)["notification-preferences"]["get"]>>["data"]
>;

/**
 * The bell's feed.
 *
 * Polled, and `refetchOnWindowFocus` is on — which is the one place in this app that
 * deviates from `lib/query.ts`. The rule it bends exists for the *calendar grid*: DESIGN.md
 * §10 forbids auto-refresh that moves content under the pointer, and a technician alt-tabbing
 * back mid-drag must not have the grid re-lay itself. A badge in the header moves nothing a
 * pointer is aimed at, and a workshop that learns the bell is stale stops looking at it.
 *
 * Thirty seconds, because the entry this exists for — "somebody booked, go confirm it" —
 * is one a customer is waiting on, and a minute of silence at the counter is a minute the
 * booking sits unanswered.
 */
export function useStaffNotifications(franchiseId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: keys.staffNotifications(franchiseId ?? ""),
    enabled: franchiseId !== null && enabled,
    queryFn: () =>
      unwrap(
        api.v1.franchises({ franchiseId: franchiseId as string })["staff-notifications"].get({
          query: {},
        }),
      ),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    staleTime: 0,
  });
}

export interface NotificationFilter {
  readonly kind: string | null;
  readonly unreadOnly: boolean;
}

/**
 * The full history, for `/aviseringar`.
 *
 * Separate from `useStaffNotifications` rather than sharing its cache: the bell wants the
 * newest thirty, polled, and the page wants every one of them under a filter that the bell
 * knows nothing about. One query serving both would either poll the whole history every
 * thirty seconds or make the bell's badge depend on which filter the page was left on.
 *
 * Not polled, and that is deliberate. This is a list somebody is reading and scrolling; a
 * refetch under the pointer would move the row they were about to click (DESIGN.md §10).
 * The bell beside it is the live surface, and it invalidates this on every write.
 */
export function useNotificationHistory(franchiseId: string | null, filter: NotificationFilter) {
  return useInfiniteQuery({
    queryKey: keys.staffNotificationHistory(franchiseId ?? "", filter.kind, filter.unreadOnly),
    enabled: franchiseId !== null,
    initialPageParam: "",
    queryFn: ({ pageParam }) =>
      unwrap(
        api.v1.franchises({ franchiseId: franchiseId as string })["staff-notifications"].get({
          query: {
            limit: 30,
            ...(pageParam === "" ? {} : { cursor: pageParam }),
            ...(filter.kind === null ? {} : { kind: filter.kind }),
            ...(filter.unreadOnly ? { unread: true } : {}),
          },
        }),
      ),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });
}

/** An empty `ids` means "all of them" — the mark-all button, not a second endpoint. */
export function useMarkNotificationsRead(franchiseId: string) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (ids: readonly string[]) =>
      unwrap(
        api.v1.franchises({ franchiseId })["staff-notifications"].read.post({ ids: [...ids] }),
      ),
    onSettled: () => {
      void client.invalidateQueries({ queryKey: keys.staffNotifications(franchiseId) });
      void client.invalidateQueries({
        queryKey: keys.staffNotificationHistoryAll(franchiseId),
      });
    },
  });
}

export function useNotificationPreferences() {
  return useQuery({
    queryKey: keys.notificationPreferences(),
    queryFn: () => unwrap(api.v1.me["notification-preferences"].get()),
    staleTime: 5 * 60_000,
  });
}

export function useSaveNotificationPreferences() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (input: {
      sound_enabled: boolean;
      desktop_enabled: boolean;
      muted_kinds: readonly string[];
    }) =>
      unwrap(
        api.v1.me["notification-preferences"].put({
          sound_enabled: input.sound_enabled,
          desktop_enabled: input.desktop_enabled,
          muted_kinds: [...input.muted_kinds],
        }),
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.notificationPreferences() });
    },
  });
}
